import { motion, useInView } from "framer-motion";
import { useRef } from "react";

// Weddings, winery tours, graduations and corporate events were removed from the
// self-serve booking form on purpose: they are quoted per event, not per mile or
// per hour, so a form that prices a transfer cannot price them honestly. This
// section is where they live now — it says what each one covers and hands the
// visitor straight to email, which is how they were always really arranged.
const EMAIL = "info@sagarluxuryride.com";

const mailto = (occasion) =>
  `mailto:${EMAIL}?subject=${encodeURIComponent(`${occasion} — transportation enquiry`)}` +
  `&body=${encodeURIComponent(
    `Hi Sagar,\n\nI'd like a quote for ${occasion.toLowerCase()} transportation.\n\n` +
      `Date:\nStart time:\nPickup location:\nDestination(s):\nNumber of guests:\nHours needed:\n\nThank you.`
  )}`;

const occasions = [
  {
    name: "Weddings",
    blurb:
      "Bridal party, family and guest transfers timed to the ceremony. Multiple vehicles coordinated across the day so nobody waits.",
    detail: "Sedans \u00b7 SUVs \u00b7 Sprinters",
    icon: (
      <path d="M12 21s-6.7-4.35-9.1-8.06C1.2 10.2 2.3 6.5 5.6 5.6c2-.54 3.9.3 4.9 1.8l1.5 2.2 1.5-2.2c1-1.5 2.9-2.34 4.9-1.8 3.3.9 4.4 4.6 2.7 7.34C18.7 16.65 12 21 12 21z" />
    ),
  },
  {
    name: "Winery Tours",
    blurb:
      "A chauffeur for the whole route through Augusta, Hermann and the Missouri wine country. Everyone tastes, nobody drives.",
    detail: "Half day or full day",
    icon: (
      <path d="M8 3h8l-.7 6.2a3.3 3.3 0 0 1-6.6 0L8 3zm4 9.5V19m-3.5 2h7" />
    ),
  },
  {
    name: "Graduation & Prom",
    blurb:
      "A safe, tracked ride for the evening, with a named chauffeur and a schedule parents can see before it starts.",
    detail: "Hourly or by the evening",
    icon: (
      <path d="M12 3 2 8l10 5 10-5-10-5zM6 11v4.5c0 1.4 2.7 2.5 6 2.5s6-1.1 6-2.5V11" />
    ),
  },
  {
    name: "Corporate Events",
    blurb:
      "Executive travel, client roadshows, conference shuttles and airport runs — invoiced to the company, arranged with one contact.",
    detail: "Account billing available",
    icon: (
      <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-4h6v4M9 11h.01M15 11h.01" />
    ),
  },
];

function OccasionCard({ occasion, index, inView }) {
  return (
    <motion.a
      href={mailto(occasion.name)}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: index * 0.08 }}
      whileHover={{ y: -6, transition: { duration: 0.25 } }}
      className="group relative flex flex-col gap-3 p-7 border border-white/5 hover:border-gold/40 transition-all duration-300 hover:shadow-xl hover:shadow-gold/5"
      style={{ background: "#1a1a1a" }}
    >
      <span className="absolute top-0 left-0 right-0 h-px bg-gold scale-x-0 group-hover:scale-x-100 transition-transform duration-400 origin-left" />

      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        className="w-8 h-8 text-gold/80 stroke-current fill-none"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {occasion.icon}
      </svg>

      <h3 className="font-display text-cream text-lg font-medium">{occasion.name}</h3>
      <p className="text-cream/50 text-sm leading-relaxed">{occasion.blurb}</p>

      <div className="mt-auto pt-4 border-t border-white/5 flex flex-col gap-2">
        <span className="text-gold/60 text-[0.7rem] tracking-wider uppercase">{occasion.detail}</span>
        <span className="text-gold text-sm font-semibold tracking-wide opacity-75 group-hover:opacity-100 transition-opacity">
          Request a quote →
        </span>
      </div>
    </motion.a>
  );
}

export default function Occasions() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <section id="occasions" className="py-24 md:py-32" style={{ background: "#0f0f0f" }}>
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-16"
        >
          <p className="text-gold tracking-[0.3em] text-xs uppercase mb-4 font-medium">
            Special Occasions
          </p>
          <h2 className="section-heading">Planned Around Your Event</h2>
          <div className="gold-divider" />
          <p className="text-cream/55 max-w-xl mx-auto mt-4 text-sm md:text-base leading-relaxed">
            Weddings, tours and events are quoted personally rather than booked by the
            mile — so the vehicles, the timings and the price are built around your day.
            Tell us the date and we'll come back with a plan.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {occasions.map((o, i) => (
            <OccasionCard key={o.name} occasion={o} index={i} inView={inView} />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.45 }}
          className="mt-14 text-center"
        >
          <p className="text-cream/40 text-xs tracking-widest uppercase mb-5">
            Planning something else?
          </p>
          <a
            href={mailto("Special occasion")}
            className="inline-flex items-center gap-3 border border-gold/40 hover:border-gold text-gold hover:text-gold-light px-8 py-4 text-sm tracking-[0.15em] uppercase transition-all duration-300 hover:shadow-lg hover:shadow-gold/10"
          >
            <svg
              viewBox="0 0 24 24"
              aria-hidden="true"
              className="w-4 h-4 stroke-current fill-none"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 5h16v14H4z" />
              <path d="m4 6 8 6 8-6" />
            </svg>
            {EMAIL}
          </a>
          <p className="text-cream/35 text-xs mt-4">
            Every enquiry is answered personally, usually the same day.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
